/**
 * Cart Drawer Module - EVSTORE
 * 
 * Slide-out mini cart with AJAX functionality.
 * Intercepts add-to-cart forms to open drawer instead of redirect.
 */

class CartDrawer {
    constructor() {
        this.drawer = document.querySelector('[data-cart-drawer]');
        if (!this.drawer) return;

        this.overlay = this.drawer.querySelector('[data-cart-drawer-close]');
        this.body = this.drawer.querySelector('[data-drawer-body]');
        this.footer = this.drawer.querySelector('[data-drawer-footer]');
        this.countEl = this.drawer.querySelector('[data-drawer-count]');
        this.subtotalEl = this.drawer.querySelector('[data-drawer-subtotal]');
        this.headerCountEl = document.querySelector('[data-cart-count]');

        this.isOpen = false;

        this.init();
    }

    init() {
        this.bindDrawerControls();
        this.bindItemActions();
        this.interceptAddToCart();
    }

    // ---- Drawer Open/Close ----

    bindDrawerControls() {
        // Open buttons (header cart icon)
        document.querySelectorAll('[data-open-cart-drawer]').forEach(btn => {
            btn.addEventListener('click', (e) => {
                e.preventDefault();
                this.open();
            });
        });

        // Close buttons (X and overlay)
        this.drawer.querySelectorAll('[data-cart-drawer-close]').forEach(btn => {
            btn.addEventListener('click', () => this.close());
        });

        // Close on Escape
        document.addEventListener('keydown', (e) => {
            if (e.key === 'Escape' && this.isOpen) this.close();
        });
    }

    open() {
        this.drawer.classList.add('is-open');
        this.drawer.setAttribute('aria-hidden', 'false');
        document.body.style.overflow = 'hidden';
        this.isOpen = true;
    }

    close() {
        this.drawer.classList.remove('is-open');
        this.drawer.setAttribute('aria-hidden', 'true');
        document.body.style.overflow = '';
        this.isOpen = false;
    }

    // ---- Item Actions (Quantity & Remove) ----

    bindItemActions() {
        this.drawer.addEventListener('click', (e) => {
            const minus = e.target.closest('[data-drawer-qty-minus]');
            const plus = e.target.closest('[data-drawer-qty-plus]');
            const remove = e.target.closest('[data-drawer-remove]');

            if (minus || plus) {
                const item = e.target.closest('[data-drawer-item]');
                if (!item) return;
                const key = item.dataset.itemKey;
                const valEl = item.querySelector('[data-drawer-qty-val]');
                const current = parseInt(valEl.textContent) || 1;
                const newQty = plus ? current + 1 : Math.max(0, current - 1);

                if (newQty === 0) {
                    this.removeItem(key, item);
                } else {
                    valEl.textContent = newQty;
                    this.updateQuantity(key, newQty, item);
                }
            }

            if (remove) {
                const item = e.target.closest('[data-drawer-item]');
                if (!item) return;
                this.removeItem(item.dataset.itemKey, item);
            }
        });
    }

    async updateQuantity(key, quantity, itemEl) {
        itemEl.classList.add('is-updating');

        try {
            const response = await fetch('/cart/change.js', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ id: key, quantity: quantity })
            });

            if (response.ok) {
                const cart = await response.json();
                this.updatePrices(cart, key, itemEl);
                this.updateTotals(cart);
            }
        } catch (err) {
            console.error('Cart update error:', err);
        } finally {
            itemEl.classList.remove('is-updating');
        }
    }

    async removeItem(key, itemEl) {
        itemEl.classList.add('is-removing');

        try {
            const response = await fetch('/cart/change.js', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ id: key, quantity: 0 })
            });

            if (response.ok) {
                const cart = await response.json();

                setTimeout(() => {
                    itemEl.remove();
                    this.updateTotals(cart);

                    if (cart.item_count === 0) {
                        this.showEmptyState();
                    }
                }, 300);
            }
        } catch (err) {
            console.error('Remove item error:', err);
            itemEl.classList.remove('is-removing');
        }
    }

    // ---- Intercept Add to Cart ----

    interceptAddToCart() {
        document.addEventListener('submit', async (e) => {
            const form = e.target.closest('form[action*="/cart/add"]');
            if (!form) return;

            // Don't intercept buy-now buttons
            if (e.submitter && e.submitter.name === 'checkout') return;

            e.preventDefault();

            const submitBtn = form.querySelector('[type="submit"]');
            const originalText = submitBtn ? submitBtn.innerHTML : '';

            if (submitBtn) {
                submitBtn.disabled = true;
                submitBtn.innerHTML = '<div class="spinner" style="width:18px;height:18px;border-width:2px;"></div>';
            }

            try {
                const formData = new FormData(form);
                const response = await fetch('/cart/add.js', {
                    method: 'POST',
                    body: formData
                });

                if (response.ok) {
                    // Refresh drawer content and open
                    await this.refreshDrawer();
                    this.open();

                    if (submitBtn) {
                        submitBtn.innerHTML = '✓ Aggiunto!';
                        setTimeout(() => {
                            submitBtn.disabled = false;
                            submitBtn.innerHTML = originalText;
                        }, 1500);
                    }
                } else {
                    const err = await response.json();
                    throw new Error(err.description || 'Errore');
                }
            } catch (err) {
                console.error('Add to cart error:', err);
                if (submitBtn) {
                    submitBtn.disabled = false;
                    submitBtn.innerHTML = originalText;
                }
                // Show notification
                this.showNotification(err.message || 'Errore nell\'aggiunta al carrello', 'error');
            }
        });
    }

    // ---- Refresh Drawer Content (full reload from server) ----

    async refreshDrawer() {
        try {
            const cartResponse = await fetch('/cart.js');
            if (!cartResponse.ok) return;

            const cart = await cartResponse.json();
            this.renderItems(cart);
            this.updateTotals(cart);
        } catch (err) {
            console.error('Refresh drawer error:', err);
        }
    }

    renderItems(cart) {
        if (cart.item_count === 0) {
            this.showEmptyState();
            return;
        }

        let html = '';
        cart.items.forEach(item => {
            const variantHtml = item.variant_title && item.variant_title !== 'Default Title'
                ? `<span class="cart-drawer__item-variant">${item.variant_title}</span>`
                : '';

            const imgHtml = item.image
                ? `<img src="${this.getSizedImageUrl(item.image, '160x')}" alt="${this.escapeHtml(item.title)}" width="70" height="70" loading="lazy">`
                : '';

            html += `
        <div class="cart-drawer__item" data-drawer-item data-item-key="${item.key}">
          <a href="${item.url}" class="cart-drawer__item-image">${imgHtml}</a>
          <div class="cart-drawer__item-info">
            <a href="${item.url}" class="cart-drawer__item-title">${this.truncate(item.product_title, 40)}</a>
            ${variantHtml}
            <div class="cart-drawer__item-bottom">
              <div class="cart-drawer__qty" data-qty-wrapper>
                <button type="button" class="cart-drawer__qty-btn" data-drawer-qty-minus aria-label="Meno">−</button>
                <span class="cart-drawer__qty-val" data-drawer-qty-val>${item.quantity}</span>
                <button type="button" class="cart-drawer__qty-btn" data-drawer-qty-plus aria-label="Più">+</button>
              </div>
              <span class="cart-drawer__item-price" data-drawer-item-price>${this.formatMoney(item.final_line_price)}</span>
            </div>
          </div>
          <button type="button" class="cart-drawer__item-remove" data-drawer-remove aria-label="Rimuovi">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2" width="16" height="16">
              <path stroke-linecap="round" stroke-linejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
          </button>
        </div>
      `;
        });

        const itemsContainer = this.body.querySelector('[data-drawer-items]');
        if (itemsContainer) {
            itemsContainer.innerHTML = html;
        } else {
            this.body.innerHTML = `<div class="cart-drawer__items" data-drawer-items>${html}</div>`;
        }

        // Show footer
        if (this.footer) this.footer.style.display = '';
    }

    showEmptyState() {
        this.body.innerHTML = `
      <div class="cart-drawer__empty" data-drawer-empty>
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5" width="48" height="48">
          <path stroke-linecap="round" stroke-linejoin="round" d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
        </svg>
        <p class="cart-drawer__empty-title">Il tuo carrello è vuoto</p>
        <a href="/" class="cart-drawer__empty-btn" data-cart-drawer-close>Scopri i Prodotti</a>
      </div>
    `;

        // Rebind close on the new button
        this.body.querySelector('[data-cart-drawer-close]')?.addEventListener('click', () => this.close());

        if (this.footer) this.footer.style.display = 'none';
    }

    // ---- Update Helpers ----

    updatePrices(cart, key, itemEl) {
        const cartItem = cart.items.find(i => i.key === key);
        if (cartItem && itemEl) {
            const priceEl = itemEl.querySelector('[data-drawer-item-price]');
            if (priceEl) priceEl.textContent = this.formatMoney(cartItem.final_line_price);
        }
    }

    updateTotals(cart) {
        if (this.countEl) this.countEl.textContent = cart.item_count;
        if (this.subtotalEl) this.subtotalEl.textContent = this.formatMoney(cart.total_price);

        // Update header count
        if (this.headerCountEl) {
            this.headerCountEl.textContent = cart.item_count;
            this.headerCountEl.style.display = cart.item_count > 0 ? '' : 'none';
        }

        // Also update the cart page if it exists
        const cartPageSubtotal = document.querySelector('[data-cart-subtotal]');
        const cartPageTotal = document.querySelector('[data-cart-total]');
        if (cartPageSubtotal) cartPageSubtotal.textContent = this.formatMoney(cart.total_price);
        if (cartPageTotal) cartPageTotal.textContent = this.formatMoney(cart.total_price);
    }

    // ---- Utilities ----

    formatMoney(cents) {
        return '€' + (cents / 100).toFixed(2);
    }

    truncate(str, len) {
        if (!str) return '';
        return str.length > len ? str.substring(0, len) + '...' : str;
    }

    escapeHtml(str) {
        const div = document.createElement('div');
        div.textContent = str;
        return div.innerHTML;
    }

    getSizedImageUrl(url, size) {
        if (!url) return '';
        // Shopify image URL sizing
        return url.replace(/\.([a-z]+)(\?|$)/, `_${size}.$1$2`);
    }

    showNotification(message, type = 'success') {
        const existing = document.querySelector('.cart-drawer-notification');
        if (existing) existing.remove();

        const el = document.createElement('div');
        el.className = `cart-drawer-notification cart-drawer-notification--${type}`;
        el.innerHTML = `<span>${message}</span>`;
        el.style.cssText = `
      position: fixed; bottom: 20px; right: 20px; z-index: 10001;
      padding: 14px 20px; border-radius: 10px; color: #fff;
      font-size: 0.9rem; font-weight: 600;
      background: ${type === 'error' ? '#ef4444' : '#10b981'};
      box-shadow: 0 10px 25px rgba(0,0,0,0.2);
      animation: cartDrawerSlideIn 0.3s ease;
    `;
        document.body.appendChild(el);
        setTimeout(() => el.remove(), 4000);
    }
}

// Initialize
document.addEventListener('DOMContentLoaded', () => {
    window.cartDrawer = new CartDrawer();
});
